import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { TextField } from "../../../../../../components/text-field";
import { SearchSelect } from "../../../../../../components/search-select-v2";
import { MultiSelectV2 } from "../../../../../../components/multi-select-v2";
import type { EventEditorFormValues } from "../event.scheme";
import { renderError, RequiredLabel, SectionTitle, type CommonProps, type Option } from "./shared";

type Props = CommonProps & {
  categoryOptions: Option[];
  subCategoryOptions: Option[];
};

export function BasicInformationSection({
  categoryOptions,
  subCategoryOptions,
  isSubmitting,
  canEditField,
}: Props) {
  const { control } = useFormContext<EventEditorFormValues>();
  const [showSubCategoryOptions, setShowSubCategoryOptions] = useState(false);
  const [showMoreInterest, setShowMoreInterest] = useState(false);
  const { t } = useTranslation();
  return (
    <section className="event-form-section">
      <SectionTitle icon={<Info className="h-4 w-4" />}>Basic Information</SectionTitle>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <RequiredLabel>{t("event.event_name")}</RequiredLabel>
              <TextField
                {...field}
                value={field.value || ""}
                className="dark:border-gray-400"
                inputClassName="dark:bg-gray-800 dark:text-white"
                disabled={isSubmitting || !canEditField("name")}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="categoryId"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel>{t("event.category")}</RequiredLabel>
              <SearchSelect
                value={field.value || ""}
                onChange={field.onChange}
                options={categoryOptions}
                disabled={isSubmitting || !canEditField("categoryId")}
                showClear={false}
                placeholder="Select category"
                searchPlaceholder="Search category..."
                emptyMessage="No category found."
                side="top"
                triggerClassName="min-h-10 dark:!bg-gray-800"
              />
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
        <Controller
          control={control}
          name="subCategoryIds"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel required={false}>{t("event.sub_category")}</RequiredLabel>
              <MultiSelectV2
                value={field.value || []}
                open={showSubCategoryOptions}
                onOpenChange={setShowSubCategoryOptions}
                onChange={field.onChange}
                options={subCategoryOptions}
                onClear={() => field.onChange([])}
                disabled={isSubmitting || !canEditField("subCategoryIds")}
                placeholder=""
                wrapBadges={false}
                className="min-h-10"
                triggerClassName="[&_.MuiOutlinedInput-root]:flex-nowrap [&_.MuiAutocomplete-input]:min-w-0"
                showExtraCount={!showMoreInterest}
                onExtraClick={() => setShowMoreInterest(true)}
              />
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
      </div>
    </section>
  );
}
